import CtaSection from '@/components/CtaSection';
import FaqAccordion from '@/components/FaqAccordion';
import Icon from '@/components/Icon';
import Layout from '@/components/Layout';
import Seo from '@/components/Seo';
import {
  comparisonRows,
  faqs,
  howToSteps,
  reasonsToBuy,
  services,
  stats,
  testimonials,
  verificationChecklist,
  whatsappLink,
  whatsappMessages,
  whatYouGet } from
'@/lib/site-data';
import { CheckCircle2, MessageCircleDashed as MessageCircle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
};

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Como comprar uma página do Instagram com a Muita Mídia',
  description: 'Passo a passo completo para comprar uma página do Instagram com segurança, da escolha do nicho até a transferência assistida.',
  step: howToSteps.map((step) => ({
    '@type': 'HowToStep',
    name: step.title,
    text: step.description
  }))
};

const serviceListSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: services.map((service, index) => ({
    '@type': 'Service',
    position: index + 1,
    name: service.title,
    description: service.shortDescription,
    provider: {
      '@type': 'Organization',
      name: 'Muita Mídia LTDA'
    }
  }))
};

function Home() {
  return (
    <Layout>
      <Seo
        title="Comprar Página do Instagram com Seguidores Reais | Muita Mídia"
        description="Onde comprar página do Instagram com segurança: Muita Mídia, empresa registrada desde 2019, +8.400 páginas vendidas, entrega imediata via WhatsApp."
        path="/"
        jsonLd={[faqSchema, howToSchema, serviceListSchema]} />


      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B0919]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.18),transparent_60%)]" />
        <div className="relative mx-auto max-w-6xl px-6 pb-16 pt-16 sm:pb-24 sm:pt-24">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-[#C4B5FD]">
              Empresa registrada desde 2019 · CNPJ ativo
            </span>
            <h1 className="mt-6 text-3xl font-bold leading-tight text-white sm:text-5xl">
              Onde comprar página do Instagram com segurança e seguidores reais
            </h1>
            <p className="mt-5 text-base leading-relaxed text-zinc-400 sm:text-lg">
              Pule anos de crescimento do zero. Receba uma página ativa, verificada e sob seu
              controle em minutos.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={whatsappLink(whatsappMessages.heroCta)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#25D366]/20 transition-transform hover:scale-105 sm:w-auto">

                <MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} />
                Comprar página pelo WhatsApp
              </a>
              





            </div>
          </div>

          {/* Stats bar */}
          <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-4">
            {stats.map((stat) =>
            <div
              key={stat.label}
              className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-5 text-center">

                <div className="text-2xl font-bold text-white sm:text-3xl">{stat.value}</div>
                <div className="mt-1 text-xs text-zinc-500 sm:text-sm">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* POR QUE COMPRAR */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-zinc-900 sm:text-3xl">
              Por que comprar uma página em vez de começar do zero
            </h2>
            <p className="mt-3 text-base text-zinc-600">
              Criar uma conta nova no Instagram hoje é mais lento e mais caro do que assumir uma
              página já formada.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {reasonsToBuy.map((reason) =>
            <div
              key={reason.title}
              className="rounded-2xl border border-zinc-100 bg-zinc-50 p-6">

                <div className="inline-flex rounded-xl bg-[#8B5CF6]/10 p-3">
                  <Icon name={reason.icon} className="h-6 w-6 text-[#7C3AED]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-zinc-900">{reason.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{reason.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* O QUE VOCÊ RECEBE */}
      <section className="bg-[#0B0919]">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">
              O que você recebe ao comprar com a Muita Mídia
            </h2>
            <p className="mt-3 text-base text-zinc-400">
              Cada compra inclui acesso completo e acompanhamento até você ter controle total do
              perfil.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {whatYouGet.map((item) =>
            <div
              key={item.title}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">

                <div className="inline-flex rounded-xl bg-[#8B5CF6]/15 p-3">
                  <Icon name={item.icon} className="h-6 w-6 text-[#C4B5FD]" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{item.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <CtaSection
        title="Pronto para escolher a sua página?"
        description="Fale agora com nossa equipe pelo WhatsApp e receba recomendações de nicho e faixa de seguidores."
        message={whatsappMessages.heroCta}
        tone="dark" />


      {/* SERVIÇOS */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-zinc-900 sm:text-3xl">Nossos serviços</h2>
            <p className="mt-3 text-base text-zinc-600">
              Muito além da venda: verificação, transferência segura, suporte e consultoria em
              cada etapa.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) =>
            <div
              key={service.slug}
              className="flex flex-col rounded-2xl border border-zinc-100 bg-zinc-50 p-6">

                <div className="inline-flex rounded-xl bg-[#8B5CF6]/10 p-3">
                  <Icon name={service.icon} className="h-6 w-6 text-[#7C3AED]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-zinc-900">{service.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-600">
                  {service.shortDescription}
                </p>
                <Link
                to={`/servicos#${service.slug}`}
                className="mt-4 text-sm font-semibold text-[#7C3AED] hover:underline">

                  Saber mais →
                </Link>
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/servicos"
              className="inline-flex items-center justify-center rounded-full border border-zinc-200 px-6 py-3 text-sm font-semibold text-zinc-900 transition-colors hover:bg-zinc-50">

              Ver todos os serviços
            </Link>
          </div>
        </div>
      </section>

      {/* PASSO A PASSO */}
      <section className="bg-[#0B0919]">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">Passo a passo da compra</h2>
            <p className="mt-3 text-base text-zinc-400">
              Do primeiro contato até você assumir o controle total da página.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {howToSteps.map((step, index) =>
            <div
              key={step.title}
              className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6">

                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#8B5CF6] text-sm font-bold text-white">
                  {index + 1}
                </span>
                <h3 className="mt-4 text-base font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{step.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VERIFICAÇÃO */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900 sm:text-3xl">
                O que verificamos antes de vender
              </h2>
              <p className="mt-3 text-base leading-relaxed text-zinc-600">
                Nenhuma página entra no nosso portfólio sem passar por uma verificação manual
                completa. É esse processo que garante seguidores reais e ausência de restrições.
              </p>
              <a
                href={whatsappLink(whatsappMessages.consultingCta)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#8B5CF6] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#7C3AED]">

                <MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} />
                Tirar dúvidas no WhatsApp
              </a>
            </div>
            <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-6 sm:p-8">
              <ul className="space-y-4">
                {verificationChecklist.map((item) =>
                <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#16A34A]" />
                    <span className="text-sm leading-relaxed text-zinc-700">{item}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARATIVO */}
      <section className="bg-[#0B0919]">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">
              Comprar com a Muita Mídia faz diferença
            </h2>
            <p className="mt-3 text-base text-zinc-400">
              Compare as opções antes de decidir onde comprar página do Instagram.
            </p>
          </div>

          <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.04] text-zinc-300">
                  <th className="px-5 py-4 font-semibold">Critério</th>
                  <th className="px-5 py-4 font-semibold text-[#C4B5FD]">Muita Mídia</th>
                  <th className="px-5 py-4 font-semibold">Vendedor sem empresa</th>
                  <th className="px-5 py-4 font-semibold">Seguidores falsos</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, index) =>
                <tr
                  key={row.feature}
                  className={index % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent'}>

                    <td className="px-5 py-4 font-medium text-white">{row.feature}</td>
                    <td className="px-5 py-4 text-[#C4B5FD]">{row.muitaMidia}</td>
                    <td className="px-5 py-4 text-zinc-400">{row.sellerWithoutCompany}</td>
                    <td className="px-5 py-4 text-zinc-400">{row.fakeFollowers}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-zinc-900 sm:text-3xl">
              Quem comprou com a Muita Mídia
            </h2>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {testimonials.map((testimonial) =>
            <div
              key={testimonial.name}
              className="rounded-2xl border border-zinc-100 bg-zinc-50 p-6">

                <div className="flex gap-1">
                  {Array.from({ length: testimonial.rating }).map((_, i) =>
                <Star key={i} className="h-4 w-4 fill-[#F59E0B] text-[#F59E0B]" />
                )}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-zinc-700">
                  "{testimonial.quote}"
                </p>
                <div className="mt-4 text-sm font-semibold text-zinc-900">{testimonial.name}</div>
                <div className="text-xs text-zinc-500">{testimonial.role}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0B0919]">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">Perguntas frequentes</h2>
            <p className="mt-3 text-base text-zinc-400">
              Tudo que você precisa saber antes de comprar sua página do Instagram.
            </p>
          </div>
          <div className="mt-10">
            <FaqAccordion faqs={faqs} tone="dark" />
          </div>
        </div>
      </section>

      <CtaSection
        title="Fale agora com a Muita Mídia"
        description="Atendimento imediato pelo WhatsApp, entrega rápida e transferência acompanhada do início ao fim."
        message={whatsappMessages.finalCta}
        tone="light" />

    </Layout>);

}

export default Home;